import { useState } from 'react';
import { Dialog } from 'primereact/dialog';
import moment from 'moment-timezone';
import {
  FiFileText,
  FiFolder,
  FiUsers,
  FiList,
  FiUser,
  FiClock,
  FiShield,
  FiCalendar,
  FiCheckCircle,
  FiXCircle,
} from 'react-icons/fi';

import { textVariantsEnum, fontWeightEnum, Text } from '@/components/Text';
import { Button, buttonVariants } from '@/components/Button';
import { StatusBadge } from '@/components/StatusBadge';
import {
  IconBadge,
  iconBadgeSizes,
  iconBadgeShapes,
} from '@/components/IconBadge';
import { ConfirmActionDialog } from '@/components/ConfirmActionDialog';
import { toDateValue } from '@/utils/firestoreDate';
import {
  getRegularizationReporterLabel,
  REG_STATUS_TONE,
} from './Regularization.helper';
import styles from './Regularization.module.scss';

const REG_STATUS_ICON_BG = {
  Pending: 'warning-100',
  Approved: 'success-100',
  Declined: 'error-100',
};
const REG_STATUS_ICON_COLOR = {
  Pending: 'var(--warning-600)',
  Approved: 'var(--success-600)',
  Declined: 'var(--error-600)',
};

function formatDetailDate(date) {
  return date ? moment(date).format('DD/MM/YYYY') : '—';
}

function DetailField({ icon, iconBg, label, value }) {
  return (
    <div className={styles.detailField}>
      <IconBadge icon={icon} bg={iconBg} size={iconBadgeSizes.sm} />
      <div className={styles.detailFieldText}>
        <Text
          variant={textVariantsEnum.Text_xs}
          fontWeight={fontWeightEnum.Medium}
          color="gray-500"
          className={styles.detailFieldLabel}
        >
          {label}
        </Text>
        <Text
          variant={textVariantsEnum.Text_sm}
          fontWeight={fontWeightEnum.Semibold}
        >
          {value || '—'}
        </Text>
      </div>
    </div>
  );
}

export const ViewRegularizationModal = ({
  isVisible,
  rowData,
  teamProjects,
  memberDetails,
  onClose,
  title = 'Regularization Request Details',
  onApprove,
  onDecline,
  approveLoading = false,
  declineLoading = false,
}) => {
  const [confirmAction, setConfirmAction] = useState(null);

  if (!rowData) return null;

  const project = teamProjects?.find((p) => p.id === rowData.projectId);
  const manager = memberDetails?.[rowData.assignee];
  const duration = `${rowData.taskHours || 0}h ${String(
    rowData.taskMinutes || 0
  ).padStart(2, '0')}m`;
  const statusIconBg = REG_STATUS_ICON_BG[rowData.status] || 'gray-100';
  const statusIconColor =
    REG_STATUS_ICON_COLOR[rowData.status] || 'var(--gray-600)';
  const isReview = Boolean(onApprove || onDecline);
  const isBusy = approveLoading || declineLoading;

  return (
    <Dialog
      visible={isVisible}
      onHide={onClose}
      closable={!isBusy}
      closeOnEscape={!isBusy}
      dismissableMask={!isBusy}
      style={{ width: '680px' }}
      header={
        <div className={styles.regDialogHeader}>
          <IconBadge
            icon={<FiFileText size={20} color="var(--indigo-600)" />}
            bg="indigo-100"
            size={iconBadgeSizes.lg}
            shape={iconBadgeShapes.circle}
          />
          <div className={styles.regDialogHeaderText}>
            <Text
              variant={textVariantsEnum.Text_lg}
              fontWeight={fontWeightEnum.Semibold}
            >
              {title}
            </Text>
          </div>
        </div>
      }
      footer={
        isReview ? (
          <div className={styles.regDialogFooter}>
            <Button
              variant={buttonVariants.primary}
              className={styles.reviewPillButton}
              text="Accept"
              onClick={() => setConfirmAction('approve')}
              disabled={isBusy}
            />
            <Button
              variant={buttonVariants.red}
              text="Decline"
              onClick={() => setConfirmAction('decline')}
              disabled={isBusy}
            />
          </div>
        ) : (
          <div className={styles.viewDialogFooter}>
            <Button
              variant={buttonVariants.secondary}
              text="Close"
              onClick={onClose}
            />
          </div>
        )
      }
    >
      <div className={styles.regFormContent}>
        <div className={styles.formRow2Col}>
          <DetailField
            icon={<FiFolder size={16} color="var(--indigo-600)" />}
            iconBg="indigo-100"
            label="Project"
            value={project?.data?.project_name || 'N/A'}
          />
          <DetailField
            icon={<FiUsers size={16} color="var(--indigo-600)" />}
            iconBg="indigo-100"
            label="Department"
            value={rowData.teamName || 'N/A'}
          />
          <DetailField
            icon={<FiList size={16} color="var(--indigo-600)" />}
            iconBg="indigo-100"
            label="Task"
            value={rowData.taskName || 'N/A'}
          />
          <DetailField
            icon={<FiUser size={16} color="var(--indigo-600)" />}
            iconBg="indigo-100"
            label="Reported By"
            value={getRegularizationReporterLabel(rowData, memberDetails)}
          />
          <DetailField
            icon={<FiClock size={16} color="var(--indigo-600)" />}
            iconBg="indigo-100"
            label="Duration"
            value={duration}
          />
          <DetailField
            icon={<FiShield size={16} color={statusIconColor} />}
            iconBg={statusIconBg}
            label="Status"
            value={
              <StatusBadge
                label={rowData.status}
                tone={REG_STATUS_TONE[rowData.status]}
              />
            }
          />
          <DetailField
            icon={<FiCalendar size={16} color="var(--indigo-600)" />}
            iconBg="indigo-100"
            label="Task Date"
            value={formatDetailDate(toDateValue(rowData.startTime))}
          />
          <DetailField
            icon={<FiCalendar size={16} color="var(--indigo-600)" />}
            iconBg="indigo-100"
            label="Report Date"
            value={formatDetailDate(
              toDateValue(rowData.createdAt || rowData.requestDate)
            )}
          />
          <div className={styles.gridColumnFull}>
            <DetailField
              icon={<FiUser size={16} color="var(--indigo-600)" />}
              iconBg="indigo-100"
              label="Manager"
              value={
                manager
                  ? `${manager.userName || manager.userEmailid}${
                      manager.userType ? ` (${manager.userType})` : ''
                    }`
                  : 'N/A'
              }
            />
          </div>
        </div>
      </div>
      {isReview && (
        <ConfirmActionDialog
          visible={confirmAction === 'approve'}
          onHide={() => setConfirmAction(null)}
          icon={<FiCheckCircle size={18} color="var(--success-600)" />}
          iconBg="success-100"
          title="Approve Regularization Request"
          message="Are you sure you want to approve this regularization request?"
          confirmLabel="Approve"
          confirmVariant={buttonVariants.primary}
          onConfirm={onApprove}
          loading={approveLoading}
        />
      )}
      {isReview && (
        <ConfirmActionDialog
          visible={confirmAction === 'decline'}
          onHide={() => setConfirmAction(null)}
          icon={<FiXCircle size={18} color="var(--error-600)" />}
          iconBg="error-100"
          title="Decline Regularization Request"
          message="Are you sure you want to decline this regularization request?"
          confirmLabel="Decline"
          onConfirm={onDecline}
          loading={declineLoading}
        />
      )}
    </Dialog>
  );
};

ViewRegularizationModal.displayName = 'ViewRegularizationModal';
